import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Alert,
  Stepper, Step, StepLabel, CircularProgress, LinearProgress
} from '@mui/material';
import { Send, NavigateNext, NavigateBefore, HowToReg, KeyboardArrowDown } from '@mui/icons-material';
import { formsAPI, draftsAPI, captchaAPI, scholarAPI, registrationAPI } from '../../services/api';
import { MultiStepFormRenderer } from '../../components/FormRenderer';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

var PHASE_IDENTITY = 0;
var PHASE_OTP = 1;
var PHASE_FORM = 2;
var PHASE_SUBMITTED = 3;

var E = React.createElement;

export default function RegistrationForm() {
  var params = useParams();
  var formId = params.formId;
  var searchParamsArr = useSearchParams();
  var searchParams = searchParamsArr[0];
  var resumeToken = searchParams.get('resume');

  // Form data
  var fs = useState(null); var form = fs[0]; var setForm = fs[1];
  var ss = useState(null); var schema = ss[0]; var setSchema = ss[1];
  var ls = useState(true); var loading = ls[0]; var setLoading = ls[1];
  var es = useState(''); var error = es[0]; var setError = es[1];
  var pss = useState({}); var parsedSettings = pss[0]; var setParsedSettings = pss[1];

  // Phase
  var phs = useState(PHASE_IDENTITY); var phase = phs[0]; var setPhase = phs[1];

  // Identity
  var ns = useState(''); var name = ns[0]; var setName = ns[1];
  var ems = useState(''); var email = ems[0]; var setEmail = ems[1];

  // OTP
  var otps = useState(''); var otp = otps[0]; var setOtp = otps[1];
  var oes = useState(''); var otpError = oes[0]; var setOtpError = oes[1];
  var oss = useState(false); var otpSent = oss[0]; var setOtpSent = oss[1];
  var ols = useState(false); var otpLoading = ols[0]; var setOtpLoading = ols[1];

  // Draft
  var dts = useState(''); var draftToken = dts[0]; var setDraftToken = dts[1];
  var ovs = useState(false); var verified = ovs[0]; var setVerified = ovs[1];

  // Scholar status
  var scs = useState(null); var scholarStatus = scs[0]; var setScholarStatus = scs[1];
  var isScholar = scholarStatus && scholarStatus.is_scholar;

  // Form filling
  var vs = useState({}); var values = vs[0]; var setValues = vs[1];
  var fls = useState({}); var files = fls[0]; var setFiles = fls[1];
  var fes = useState({}); var fieldErrors = fes[0]; var setFieldErrors = fes[1];
  var cst = useState(0); var currentStep = cst[0]; var setCurrentStep = cst[1];
  var hs = useState([]); var history = hs[0]; var setHistory = hs[1];
  var sbs = useState(false); var submitting = sbs[0]; var setSubmitting = sbs[1];
  var svs = useState(false); var saving = svs[0]; var setSaving = svs[1];
  var lss = useState(null); var lastSaved = lss[0]; var setLastSaved = lss[1];
  var rls = useState(''); var resumeLink = rls[0]; var setResumeLink = rls[1];

  var autoSaveTimer = useRef(null);

  // CAPTCHA
  var cqs = useState(''); var captchaQuestion = cqs[0]; var setCaptchaQuestion = cqs[1];
  var cts = useState(''); var captchaToken = cts[0]; var setCaptchaToken = cts[1];
  var cas = useState(''); var captchaAnswer = cas[0]; var setCaptchaAnswer = cas[1];
  var ces = useState(false); var captchaEnabled = ces[0]; var setCaptchaEnabled = ces[1];

  // Scroll hint
  var shs = useState(false); var showScrollHint = shs[0]; var setShowScrollHint = shs[1];

  useEffect(function() {
    function update() {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      var nearBottom = window.scrollY >= scrollable - 80;
      setShowScrollHint(scrollable > 120 && !nearBottom);
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    var t = setInterval(update, 800);
    return function() {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      clearInterval(t);
    };
  }, []);

  // Load form
  useEffect(function() {
    formsAPI.getPublic(formId)
      .then(function(r) {
        var f = r.data.data;
        if (f.form_type !== 'registration') {
          setError('This is not a registration form. Use /apply/' + formId + ' instead.');
          setLoading(false);
          return;
        }
        setForm(f);
        var s = typeof f.schema === 'string' ? JSON.parse(f.schema) : f.schema;
        if (typeof s === 'string') { try { s = JSON.parse(s); } catch(ep) { s = {}; } }
        setSchema(s);
        var settings = typeof f.settings === 'string' ? JSON.parse(f.settings) : (f.settings || {});
        if (typeof settings === 'string') { try { settings = JSON.parse(settings); } catch(ep2) { settings = {}; } }
        setParsedSettings(settings);
        setCaptchaEnabled(!!settings.enable_captcha);
        if (settings.enable_captcha) {
          captchaAPI.generate().then(function(cr) {
            setCaptchaQuestion(cr.data.data.question);
            setCaptchaToken(cr.data.data.token);
          }).catch(function(e1) {});
        }
        setLoading(false);
      })
      .catch(function(err) {
        setError((err.response && err.response.data && err.response.data.error) || 'Form not found');
        setLoading(false);
      });
  }, [formId]);

  // Resume from token
  useEffect(function() {
    if (!resumeToken || !formId) return;
    draftsAPI.loadDraft(formId, resumeToken)
      .then(function(r) {
        var d = r.data.data;
        setEmail(d.email);
        if (d.name) setName(d.name);
        setDraftToken(resumeToken);
        setVerified(d.verified);
        if (d.responses) {
          var resp = typeof d.responses === 'string' ? JSON.parse(d.responses) : d.responses;
          if (resp) setValues(resp);
        }
        if (d.verified) {
          setPhase(PHASE_FORM);
          setResumeLink(window.location.origin + '/register/' + formId + '?resume=' + resumeToken);
        }
        // Also check scholar status for the resumed email
        if (d.email) {
          scholarAPI.checkStatus(formId, d.email).then(function(sr) {
            setScholarStatus(sr.data.data);
          }).catch(function(e3) {});
        }
      })
      .catch(function(err) {
        setError((err.response && err.response.data && err.response.data.error) || 'Could not load draft');
      });
  }, [resumeToken, formId]);

  // Check scholar status and send OTP
  function checkScholarAndSendOTP() {
    setOtpLoading(true);
    setOtpError('');
    scholarAPI.checkStatus(formId, email)
      .then(function(r) {
        var status = r.data.data;
        setScholarStatus(status);
        return draftsAPI.sendOTP(formId, email, name);
      })
      .then(function() {
        setOtpSent(true);
        setPhase(PHASE_OTP);
        setOtpLoading(false);
      })
      .catch(function(err) {
        var msg = (err.response && err.response.data && err.response.data.error) || 'Failed';
        setOtpError(msg);
        setOtpLoading(false);
      });
  }

  // Verify OTP
  function verifyOTP() {
    setOtpLoading(true);
    setOtpError('');
    draftsAPI.verifyOTP(formId, email, otp)
      .then(function(r) {
        var data = r.data.data;
        setDraftToken(data.draft_token);
        setVerified(true);
        // Best-effort restore of a previously saved draft
        draftsAPI.loadDraft(formId, data.draft_token).then(function(dr) {
          var d = dr.data.data;
          if (d && d.name) setName(d.name);
          if (d && d.responses) {
            var resp = typeof d.responses === 'string' ? JSON.parse(d.responses) : d.responses;
            if (resp) setValues(resp);
          }
          if (d && d.last_section_index && d.last_section_index > 0) {
            setCurrentStep(d.last_section_index);
          }
        }).catch(function(e2) { /* no saved draft */ });
        setPhase(PHASE_FORM);
        setResumeLink(window.location.origin + '/register/' + formId + '?resume=' + data.draft_token);
        setOtpLoading(false);
      })
      .catch(function(err) {
        setOtpError((err.response && err.response.data && err.response.data.error) || 'Verification failed');
        setOtpLoading(false);
      });
  }

  // Field change
  function handleFieldChange(fieldId, value) {
    setValues(function(prev) {
      var next = Object.assign({}, prev);
      next[fieldId] = value;
      return next;
    });
    setFieldErrors(function(prev) {
      var next = Object.assign({}, prev);
      delete next[fieldId];
      return next;
    });
  }

  // File change
  function handleFileChange(fieldId, file) {
    if (file) {
      setFiles(function(prev) { var next = Object.assign({}, prev); next[fieldId] = file; return next; });
      setValues(function(prev) { var next = Object.assign({}, prev); next[fieldId] = file; return next; });
    } else {
      setFiles(function(prev) { var next = Object.assign({}, prev); delete next[fieldId]; return next; });
      setValues(function(prev) { var next = Object.assign({}, prev); next[fieldId] = ''; return next; });
    }
  }

  // Auto-save with indicator
  var autoSave = useCallback(function() {
    if (!draftToken || !verified) return;
    if (Object.keys(values).length === 0 && !name) return;
    setSaving(true);
    var draftValues = {};
    Object.keys(values).forEach(function(k) {
      if (!(values[k] instanceof File)) { draftValues[k] = values[k]; }
    });
    draftsAPI.saveDraft(formId, draftToken, draftValues, name)
      .then(function() { setLastSaved(new Date()); })
      .catch(function(e4) { /* silent */ })
      .then(function() { setSaving(false); });
  }, [draftToken, verified, values, name, formId]);

  useEffect(function() {
    if (!verified || !draftToken) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(autoSave, 3000);
    return function() { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [values, name, autoSave, verified, draftToken]);

  // ── Validation ──
  function countWords(text) {
    if (!text || typeof text !== 'string') return 0;
    return text.trim().split(/\s+/).filter(function(w) { return w.length > 0; }).length;
  }

  function validateStep() {
    setError('');
    // Registration forms collect the name through their own fields, not here
    if (!schema) return true;
    var secs = (schema.sections || []).slice().sort(function(a, b) { return a.order - b.order; });
    var section = secs[currentStep];
    if (!section) return true;

    var sectionFields = (schema.fields || []).filter(function(f) { return f.section_id === section.id; });
    var errs = {};
    sectionFields.forEach(function(field) {
      if (field.required && !values[field.id] && field.type !== 'file_upload' && field.type !== 'video') {
        errs[field.id] = 'This field is required';
      }
      if (field.required && field.type === 'file_upload' && !files[field.id] && !values[field.id]) {
        errs[field.id] = 'Please upload a file';
      }
      var v = field.validation || {};
      var val = values[field.id];
      if ((v.min_words || v.max_words) && typeof val === 'string' && val.trim()) {
        var wc = countWords(val);
        if (v.min_words && wc < v.min_words) {
          errs[field.id] = 'Please write at least ' + v.min_words + ' words (currently ' + wc + ').';
        } else if (v.max_words && wc > v.max_words) {
          errs[field.id] = 'Please keep this under ' + v.max_words + ' words (currently ' + wc + ').';
        }
      }
    });
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ── Submit ──
  function handleSubmit() {
    if (!validateStep()) return;
    setSubmitting(true);
    setError('');

    var cleanValues = {};
    Object.keys(values).forEach(function(k) {
      if (!(values[k] instanceof File)) { cleanValues[k] = values[k]; }
    });

    // Final save
    var savePromise = draftToken
      ? draftsAPI.saveDraft(formId, draftToken, cleanValues, name).catch(function(e5) {})
      : Promise.resolve();

    savePromise.then(function() {
      var hasFiles = Object.keys(files).length > 0;
      var submitPromise;

      if (hasFiles) {
        var formData = new FormData();
        formData.append('form_id', formId);
        formData.append('email', email);
        formData.append('name', name);
        formData.append('responses', JSON.stringify(cleanValues));
        formData.append('participant_type', isScholar ? 'scholar' : 'self_funded');
        if (captchaEnabled) {
          formData.append('captcha_token', captchaToken);
          formData.append('captcha_answer', captchaAnswer);
        }
        Object.keys(files).forEach(function(fieldId) {
          formData.append(fieldId, files[fieldId]);
        });
        submitPromise = registrationAPI.submit(formData);
      } else {
        var submitData = {
          form_id: formId,
          email: email,
          name: name,
          responses: cleanValues,
          participant_type: isScholar ? 'scholar' : 'self_funded',
        };
        if (captchaEnabled) {
          submitData.captcha_token = captchaToken;
          submitData.captcha_answer = captchaAnswer;
        }
        submitPromise = registrationAPI.submit(submitData);
      }

      return submitPromise;
    })
    .then(function() {
      setPhase(PHASE_SUBMITTED);
      setSubmitting(false);
    })
    .catch(function(err) {
      setError((err.response && err.response.data && err.response.data.error) || 'Submission failed');
      setSubmitting(false);
    });
  }

  // ── Render: loading / error / submitted ──

  if (loading) return E(Box, {sx:{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}, E(CircularProgress));

  if (error && !form) return E(Box, {sx:{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}},
    E(Card, {sx:{maxWidth:500,mx:2}}, E(CardContent, {sx:{p:4,textAlign:'center'}},
      E(Typography, {variant:'h5',color:'error',sx:{mb:2}}, 'Error'),
      E(Typography, null, error))));

  if (phase === PHASE_SUBMITTED) return E(Box, {sx:{minHeight:'100vh',bgcolor:'#f5f6fa',py:4}},
    E(Container, {maxWidth:'sm'},
      E(Card, null, E(CardContent, {sx:{p:4,textAlign:'center'}},
        E(HowToReg, {sx:{fontSize:64,color:'success.main',mb:2}}),
        E(Typography, {variant:'h4',sx:{mb:1}}, 'Registration Complete!'),
        E(Typography, {color:'text.secondary'},
          isScholar
            ? 'You have been registered as a scholarship recipient.'
            : 'You have been registered as a self-funded participant.'),
        E(Typography, {variant:'body2',color:'text.secondary',sx:{mt:2}}, 'A confirmation email will be sent to ' + email)))));

  // ── Derived values ──
  var sections = schema ? (schema.sections || []).slice().sort(function(a, b) { return a.order - b.order; }) : [];
  var totalSteps = sections.length;
  var formSettings = parsedSettings;

  var bgStyle = formSettings.bg_image_url ? {
    backgroundImage: 'url(' + formSettings.bg_image_url + ')',
    backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
  } : {};
  var bgOpacityRaw = formSettings.bg_opacity;
  var bgOpacityPct = (bgOpacityRaw === undefined || bgOpacityRaw === null || bgOpacityRaw === '') ? 15 : Number(bgOpacityRaw);
  if (isNaN(bgOpacityPct)) bgOpacityPct = 15;
  var bgOverlay = formSettings.bg_image_url ? { position: 'relative', '&::before': {
    content: '""', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    bgcolor: 'background.default', opacity: (100 - bgOpacityPct) / 100, zIndex: 0,
  }} : {};

  // ── Conditional section branching ──
  function resolveNext(section) {
    if (!section) return '__submit__';
    var secFields = (schema && schema.fields ? schema.fields : []).filter(function(f) { return f.section_id === section.id; });
    var branchField = null;
    for (var i = 0; i < secFields.length; i++) {
      if (secFields[i].type === 'radio' && secFields[i].branch_enabled && secFields[i].branch_map) {
        branchField = secFields[i];
        break;
      }
    }
    if (branchField) {
      var chosen = values[branchField.id];
      if (chosen && branchField.branch_map[chosen]) return branchField.branch_map[chosen];
    }
    if (section.next_section) return section.next_section;
    return '__default__';
  }

  var currentSection = sections[currentStep];
  var nextDest = resolveNext(currentSection);
  var isSubmitStep = totalSteps === 0 || nextDest === '__submit__' ||
    (nextDest === '__default__' && currentStep === totalSteps - 1);

  function goNext() {
    if (!validateStep()) return;
    var dest = resolveNext(sections[currentStep]);
    if (dest === '__submit__') { handleSubmit(); return; }
    var nextIndex;
    if (dest === '__default__') {
      nextIndex = currentStep + 1;
    } else {
      nextIndex = -1;
      for (var i = 0; i < sections.length; i++) {
        if (sections[i].id === dest) { nextIndex = i; break; }
      }
      if (nextIndex < 0) nextIndex = currentStep + 1;
    }
    if (nextIndex >= sections.length) { handleSubmit(); return; }
    setHistory(function(h) { return h.concat([currentStep]); });
    setCurrentStep(nextIndex);
    window.scrollTo(0, 0);
  }

  function goPrev() {
    setHistory(function(h) {
      if (h.length === 0) return h;
      var copy = h.slice();
      var prev = copy.pop();
      setCurrentStep(prev);
      return copy;
    });
    setFieldErrors({});
    window.scrollTo(0, 0);
  }

  var pathIndices = history.concat([currentStep]);

  // ── Render ──
  return E(Box, {sx: Object.assign({minHeight:'100vh', bgcolor:'#f5f6fa', py:4}, bgStyle, bgOverlay)},
    E(Container, {maxWidth:'md', sx:{position:'relative', zIndex:1}},

      // Header card
      E(Card, {sx:{mb:3}},
        E(CardContent, {sx:{py:4}},
          formSettings.banner_url ? E(Box, {sx:{mb:2}},
            E('img', {src:formSettings.banner_url, alt:'', style:{width:'100%',maxHeight:200,objectFit:'cover',borderRadius:8}})) : null,
          E(Typography, {variant:'h4',color:'primary',sx:{mb:1}}, form && form.title),
          // Description: only show before OTP verification
          phase === PHASE_IDENTITY && form && form.description
            ? (/<[a-z][\s\S]*>/i.test(form.description)
              ? E(Typography, {variant:'body1', color:'text.secondary', sx:{mb:1},
                  dangerouslySetInnerHTML:{__html: form.description}})
              : E(Typography, {variant:'body1', color:'text.secondary', sx:{mb:1, whiteSpace:'pre-wrap'}}, form.description))
            : null,
          // Deadline
          form && form.deadline
            ? E(Typography, {variant:'body2', color:'error'},
                'Deadline: ' + dayjs.utc(form.deadline).format('MMMM D, YYYY [at] h:mm A') + ' (UTC)',
                formSettings.deadline_tz_link
                  ? E('span', null, ' - ',
                      E('a', {href:formSettings.deadline_tz_link, target:'_blank', rel:'noopener noreferrer',
                        style:{color:'inherit', textDecoration:'underline'}}, 'find it in your local time'))
                  : null)
            : null)),

      error && phase !== PHASE_FORM ? E(Alert, {severity:'error', sx:{mb:2}}, error) : null,

      // ── Phase: Identity ──
      phase === PHASE_IDENTITY ? E(Card, null,
        E(CardContent, {sx:{p:4}},
          E(Typography, {variant:'h6', sx:{mb:2}}, 'Enter your email to register'),
          otpError ? E(Alert, {severity:'error', sx:{mb:2}}, otpError) : null,
          scholarStatus && !scholarStatus.is_scholar && !scholarStatus.allow_self_fund
            ? E(Alert, {severity:'warning', sx:{mb:2}}, 'Registration is only open for accepted scholars.')
            : null,
          E(TextField, {fullWidth:true, label:'Email Address', type:'email', value:email,
            onChange:function(e) { setEmail(e.target.value); },
            onKeyDown:function(e) { if (e.key === 'Enter' && email) { e.preventDefault(); checkScholarAndSendOTP(); } },
            sx:{mb:2}}),
          E(Typography, {variant:'body2', color:'text.secondary', sx:{mb:2}},
            "We'll email you a verification code to confirm your address before you register."),
          E(Button, {variant:'contained', size:'large', fullWidth:true, startIcon:E(Send),
            onClick:checkScholarAndSendOTP, disabled:otpLoading || !email},
            otpLoading ? 'Checking...' : 'Send Verification Code')))
      : null,

      // ── Phase: OTP ──
      phase === PHASE_OTP ? E(Card, null,
        E(CardContent, {sx:{p:4, textAlign:'center'}},
          E(Typography, {variant:'h6', sx:{mb:1}}, 'Verify your email'),
          E(Typography, {variant:'body2', color:'text.secondary', sx:{mb:2}},
            'A 6-digit code was sent to ' + email),
          otpError ? E(Alert, {severity:'error', sx:{mb:2}}, otpError) : null,
          scholarStatus ? E(Alert, {severity:isScholar ? 'success' : 'info', sx:{mb:2}},
            isScholar
              ? 'Welcome! You are registered as a scholarship recipient (' + scholarStatus.scholar_name + ')'
              : 'You will be registered as a self-funded participant.')
            : null,
          E(TextField, {fullWidth:false, label:'6-digit verification code', value:otp,
            onChange:function(e) { setOtp(e.target.value.replace(/\D/g,'').slice(0,6)); },
            onKeyDown:function(e) { if (e.key === 'Enter' && otp.length === 6) { e.preventDefault(); verifyOTP(); } },
            inputProps:{maxLength:6, style:{textAlign:'center', fontSize:24, letterSpacing:8}},
            sx:{mb:2, maxWidth:240, mx:'auto'}, autoFocus:true}),
          E(Box, {sx:{display:'flex', gap:2, justifyContent:'center'}},
            E(Button, {variant:'outlined', onClick:function() { setPhase(PHASE_IDENTITY); setOtp(''); setOtpError(''); }}, 'Back'),
            E(Button, {variant:'contained', onClick:verifyOTP, disabled:otpLoading || otp.length < 6},
              otpLoading ? 'Verifying...' : 'Verify & Continue')),
          E(Button, {size:'small', sx:{mt:2}, onClick:checkScholarAndSendOTP}, 'Resend code')))
      : null,

      // ── Phase: Form ──
      phase === PHASE_FORM ? E(React.Fragment, null,

        // Progress stepper (branch-aware)
        totalSteps > 1 ? E(Card, {sx:{mb:2}},
          E(CardContent, {sx:{py:2}},
            E(Stepper, {activeStep:history.length, alternativeLabel:true},
              pathIndices.map(function(idx, i) {
                return E(Step, {key:i, completed:i < history.length},
                  E(StepLabel, null, sections[idx] && sections[idx].title));
              })))) : null,

        // Auto-save indicator
        E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'center', mb:1, px:1}},
          E(Typography, {variant:'caption', color:'text.secondary'}, 'Logged in as ' + email),
          E(Typography, {variant:'caption', color:'text.secondary'},
            saving ? 'Saving...' : lastSaved ? 'Auto-saved ' + dayjs(lastSaved).format('h:mm A') : '')),

        // Resume link banner
        resumeLink ? E(Alert, {severity:'info', sx:{mb:2}},
          'Your progress is saved automatically. Copy and save this link (',
          E(Typography, {component:'a', href:resumeLink, variant:'body2', sx:{fontWeight:600}}, 'here'),
          ') to resume later.') : null,

        // Current section card
        E(Card, null,
          E(CardContent, {sx:{p:4}},
            // Section title
            totalSteps > 0 && sections[currentStep]
              ? E(Typography, {variant:'h6', sx:{mb:3, color:'primary.main'}},
                  sections[currentStep] && sections[currentStep].title)
              : null,

            // Fields
            E(MultiStepFormRenderer, {
              schema:schema, values:values, onChange:handleFieldChange,
              onFileChange:handleFileChange, errors:fieldErrors, currentStep:currentStep,
              scholarOnly:isScholar
            }),

            error ? E(Alert, {severity:'error', sx:{mt:2, mb:2}}, error) : null,

            // CAPTCHA on submit step
            captchaEnabled && isSubmitStep
              ? E(Box, {sx:{mb:3, p:2, border:'1px solid', borderColor:'divider', borderRadius:1}},
                  E(Typography, {variant:'body2', sx:{mb:1, fontWeight:500}}, captchaQuestion),
                  E(TextField, {size:'small', label:'Your answer', value:captchaAnswer,
                    onChange:function(e) { setCaptchaAnswer(e.target.value); }, sx:{width:140}}))
              : null,

            // Navigation buttons
            E(Box, {sx:{display:'flex', justifyContent:'space-between', mt:2}},
              E(Button, {variant:'outlined', startIcon:E(NavigateBefore),
                onClick:goPrev, disabled:history.length === 0}, 'Previous'),
              !isSubmitStep
                ? E(Button, {variant:'contained', endIcon:E(NavigateNext), onClick:goNext}, 'Next')
                : E(Button, {variant:'contained', size:'large', startIcon:E(HowToReg),
                    onClick:handleSubmit, disabled:submitting},
                    submitting ? 'Registering...' : 'Complete Registration')),

            // Progress bar
            totalSteps > 1
              ? E(LinearProgress, {variant:'determinate',
                  value:isSubmitStep ? 100 : Math.min(100, (pathIndices.length / totalSteps) * 100),
                  sx:{mt:3, borderRadius:1, height:6}})
              : null)))
      : null),

    // Scroll-down hint
    showScrollHint
      ? E(Box, {
          onClick:function() { window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' }); },
          'aria-hidden':'true',
          sx:{
            position:'fixed', right:24, bottom:24, zIndex:1300,
            width:44, height:44, borderRadius:'50%',
            bgcolor:'primary.main', color:'#fff',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:3, cursor:'pointer', opacity:0.85,
            animation:'okcbounce 1.6s infinite',
            '@keyframes okcbounce': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(6px)' },
            },
            '&:hover': { opacity: 1 },
          }}, E(KeyboardArrowDown))
      : null);
}
